import { useState, useMemo, useEffect, RefObject } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AppRoutes } from 'app/app-routes'
import { useDispatch, useSelector } from 'react-redux'
import { setActiveChatId, setScopeId, setCurrentSpaceId } from 'features/chats/chatSlice'
import type { RootState } from 'app/store'
import { useGetChatsQuery, useCreateChatMutation, useDeleteChatMutation } from 'api/chats-api'
import { useGetSpacesQuery, useGetSpaceQuery } from 'api/spaces-api'
import { useQueryParams } from 'hooks/useQueryParams'
import { useWorkspace } from 'hooks/useWorkspace'
import { useParams } from 'react-router-dom'
import { Pto } from '@outcoreaix/pto'
import { toast } from 'react-toastify'
import { authStorage } from 'app/auth-storage'

/** CustomEvent: NavMenu and Chat use separate useChatMenu instances; Chat listens and dismisses splash. */
export const CHAT_DISMISS_SPLASH_EVENT = 'outcore-chat-dismiss-splash'

function emitChatDismissSplash() {
  window.dispatchEvent(new CustomEvent(CHAT_DISMISS_SPLASH_EVENT))
}

type UseChatMenuProps = {
  onChatSwitch?: (chatId: number | undefined) => void
  onResetMessages?: () => void
  onAbortStream?: () => void
  messageInputRef?: RefObject<HTMLInputElement>
}

export const useChatMenu = ({
  onChatSwitch,
  onResetMessages,
  onAbortStream,
  messageInputRef
}: UseChatMenuProps = {}) => {
  const [page] = useState(1)
  const dispatch = useDispatch()
  const activeChatId = useSelector((state: RootState) => state.chat.activeChatId)
  const contextScopeId = useSelector((state: RootState) => state.chat.scopeId)
  const currentSpaceId = useSelector((state: RootState) => state.chat.currentSpaceId)
  const { removeParams } = useQueryParams()
  const { currentWorkspaceId } = useWorkspace()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const isSystemAdmin = authStorage.user?.roles.includes(Pto.Users.UserRole.SystemAdmin)

  const { data: spacesData } = useGetSpacesQuery(
    {
      workspaceId: currentWorkspaceId!
    },
    { skip: currentWorkspaceId === undefined }
  )

  const activeSpace = useMemo(() => {
    if (isSystemAdmin) return undefined

    const spaces = spacesData?.items.filter((item) => item.workspaceId === Number(currentWorkspaceId))

    if (params.spaceId) {
      const spaceFromUrl = spaces?.find((space) => space.id === Number(params.spaceId))
      if (spaceFromUrl) {
        dispatch(setCurrentSpaceId(spaceFromUrl.id))
        return spaceFromUrl
      }
    }

    if (currentSpaceId) {
      const spaceFromRedux = spaces?.find((space) => space.id === currentSpaceId)
      if (spaceFromRedux) {
        return spaceFromRedux
      }
    }

    const mostRecentSpace = spaces?.find((space) => space.status === Pto.Spaces.SpaceStatus.Active)

    if (mostRecentSpace) {
      dispatch(setCurrentSpaceId(mostRecentSpace.id))
      return mostRecentSpace
    }

    return undefined
  }, [spacesData, currentWorkspaceId, params.spaceId])

  const scope = Pto.Chats.ChatScope.Space
  const scopeId = activeSpace?.id

  const { data: spaceData } = useGetSpaceQuery({ spaceId: scopeId || 0 }, { skip: !scopeId })

  useEffect(() => {
    if (scopeId !== contextScopeId) {
      dispatch(setScopeId(scopeId))
    }
  }, [scopeId, contextScopeId, dispatch])

  const { data: chatsData, refetch: chatsRefetch } = useGetChatsQuery(
    {
      scope,
      scopeId: scopeId || 0,
      page,
      size: 50
    },
    { skip: !scope || !scopeId }
  )

  const chats = useMemo(() => {
    if (!scopeId) return undefined
    return chatsData
  }, [scopeId, chatsData])

  const [createChat, { isLoading: isCreateChatLoading }] = useCreateChatMutation()
  const [deleteChat] = useDeleteChatMutation()

  const switchChats = (chatId: number) => {
    if (chatId === undefined || (chatId && chatId !== activeChatId)) {
      onAbortStream?.()
      onResetMessages?.()
      dispatch(setActiveChatId(chatId))
      emitChatDismissSplash()
      onChatSwitch?.(chatId)

      if (scopeId && currentWorkspaceId) {
        const targetPath = `${AppRoutes.workspaces}/${currentWorkspaceId}/space/${scopeId}`
        if (location.pathname !== targetPath) {
          navigate(targetPath)
        }
      }
    }
  }

  const handleCreateChat = async () => {
    if (!scopeId) return

    onAbortStream?.()
    removeParams([Pto.App.ParamKeyEnum.FlowId, Pto.App.ParamKeyEnum.MessageSource, Pto.App.ParamKeyEnum.StarterId])

    const previousChatId = activeChatId
    dispatch(setActiveChatId(undefined))

    return createChat({ scope, scopeId })
      .unwrap()
      .then(async (chat) => {
        dispatch(setActiveChatId(chat.id))
        emitChatDismissSplash()
        onChatSwitch?.(chat.id)

        if (messageInputRef?.current) {
          messageInputRef.current.focus()
        }

        if (scopeId && currentWorkspaceId) {
          const targetPath = `${AppRoutes.workspaces}/${currentWorkspaceId}/space/${scopeId}`
          if (location.pathname !== targetPath) {
            navigate(targetPath)
          }
        }

        return chat
      })
      .catch(() => {
        toast.error('Failed to create a new chat')
        dispatch(setActiveChatId(previousChatId))
      })
  }

  const handleDeleteChat = async (chatId: number) => {
    const wasActiveChat = activeChatId === chatId

    if (wasActiveChat) {
      dispatch(setActiveChatId(undefined))
      emitChatDismissSplash()
    }

    await deleteChat(chatId)
      .unwrap()
      .then(() => {
        toast.success('Chat deleted')
        if (wasActiveChat) {
          onResetMessages?.()
        }
      })
      .catch(() => {})
  }

  return {
    chats,
    activeChatId,
    isCreateChatLoading,
    switchChats,
    handleCreateChat,
    handleDeleteChat,
    chatsRefetch,
    setActiveChatId,
    activeSpace,
    scopeId,
    isAIConfigSet: !!spaceData?.aiConfig?.apiKey
  }
}
